import {TextButton} from "@FRAMEWORK/ui/desktop/button";
import {PopupContainer} from "@FRAMEWORK/ui/desktop/popup";
import {TableContainer} from "@FRAMEWORK/ui/desktop/table";
import {RedText} from "@FRAMEWORK/ui/desktop/text";
import {Radio} from "@FRAMEWORK/ui/desktop/input";
import {BulletListItem} from "@FRAMEWORK/ui/desktop/list";
import Print, {PrintArea} from "@FRAMEWORK/ui/desktop/print/Print";
import {EditorBox} from "@FRAMEWORK/ui/desktop/editor";
import * as style from "@COMPONENTS/style/detailpackage/keyinformation";
import {OverlayState} from "@/hooks/common/useOverlay";
import {EventDetail} from "@QUERIES/prdt/event/useEventDetail";
import dayjs from "dayjs";
import {EventPrice} from "@QUERIES/prdt/event/useEventPrice";
import {format} from "@yb/utils";
import {getPrice, isGoodsTpFH} from "@COMPONENTS/common/eventUtil";
import useEventOptionalTourDetail from "@QUERIES/prdt/event/useEventOptionalTourDetail";
import {useEffect, useState} from "react";
import useConfigureAxios from "@FRAMEWORK/utils/useConfigureAxios";
import publicRuntimeConfig from "@FRAMEWORK/config/public";
import ErrorBoundary from "@COMPONENTS/common/error/ErrorBoundary";

interface SafetyType {
	attInfo: string;
	cntlInfo: string;
	limtInfo: string;
	banNote: string;
	splimitInfo: string;
	safeDate: string;
}

interface Params {
	event: EventDetail;
	price: EventPrice;
}

const KeyInformationPopComponent = (props: OverlayState & Params) => {
	const {event, price, ...overlayProps} = props;
	const {evNm, evStartDt, evArriveDt, startCharDivi, goodsTp, minCnt, webCancelCommiYn, webStartConfYn, shopCnt} =
		event;
	const {evCd, webTourDayYn, webGoodsPriceYn, webAirConfYn, webGuidConfYn, webAccommConfYn, webInsolConfYn} = event;
	const {adtPrice, bafAdtCost, chdPrice, bafChdCost, infPrice, bafInfCost} = price;
	const {guideExpAmt, expCurrCd, driverAmt, airTaxAdtCost, airTaxChdCost, airTaxInfCost} = price;
	const totalPice = getPrice(price);

	const useEventOptTourDtl = useEventOptionalTourDetail(evCd);
	const evtOptTourDtl = useEventOptTourDtl.data?.body;
	const shop = evtOptTourDtl?.shopList || [];
	const shopInfo = evtOptTourDtl?.optionalTourInfo?.shopInfo;

	const isFH = isGoodsTpFH(goodsTp); // 허니문 에어텔 여부

	const [safety, setSafety] = useState<SafetyType>(null); // 안전정보

	/* 안전 정보 api */
	const SafetyInfoAsync = async () => {
		await useConfigureAxios({
			url: `${publicRuntimeConfig.ENV_CHORUS_API_BASEPATH}/trvSafe/selectTrvSafe`,
			method: "POST",
			data: {
				compId: "1",
				evCd,
			},
		}).then(result => {
			if (result.data.errorCode === "0") {
				const res = result.data.trvSafeList?.reduce(
					(acc, cur) => {
						const {attentionNote, controlNote, limitaNote, banNote, splimitNote, naKorNm, trvSafeUpdDt} = cur;
						attentionNote && (acc.attInfo += `[${naKorNm}] ${attentionNote} <br/>`);
						controlNote && (acc.cntlInfo += `[${naKorNm}] ${controlNote} <br/>`);
						limitaNote && (acc.limtInfo += `[${naKorNm}] ${limitaNote} <br/>`);
						banNote && (acc.banNote += `[${naKorNm}] ${banNote} <br/>`);
						splimitNote && (acc.splimitInfo += `[${naKorNm}] ${splimitNote} <br/>`);
						acc.safeDate === "" && (acc.safeDate += trvSafeUpdDt);
						return acc;
					},
					{
						attInfo: "",
						cntlInfo: "",
						limtInfo: "",
						banNote: "",
						splimitInfo: "",
						safeDate: "",
					},
				);
				setSafety(res);
			}
		});
	};

	useEffect(() => {
		SafetyInfoAsync();
	}, []);

	return (
		<Print>
			<PopupContainer {...overlayProps} title="여행상품 핵심정보" size="large">
				<style.BtnArea>
					<TextButton text="인쇄하기" size="default" onClick={() => window.print()} />
				</style.BtnArea>
				<PrintArea id="printPopup">
					<TableContainer line={false}>
						<caption>여행상품 핵심 정보</caption>
						<colgroup>
							<col style={{width: "18%"}} />
							<col style={{width: "32%"}} />
							<col style={{width: "18%"}} />
							<col style={{width: "32%"}} />
						</colgroup>
						<tbody>
							<tr>
								<th scope="row">상품명</th>
								<td colSpan={3}>
									<style.ProductTitle>{evNm}</style.ProductTitle>
								</td>
							</tr>
							<tr>
								<th scope="row">여행 기간</th>
								<td>
									{dayjs(evStartDt).format("YYYY.MM.DD")} ~ {dayjs(evArriveDt).format("YYYY.MM.DD")}
								</td>
								<th scope="row">상품종류</th>
								<td>
									<style.RadioList>
										<li>
											<Radio htmlFor="01" disabled defaultChecked={startCharDivi === "10"}>
												단독상품
											</Radio>
										</li>
										<li>
											<Radio htmlFor="02" disabled defaultChecked={startCharDivi === "20"}>
												연합상품
											</Radio>
										</li>
									</style.RadioList>
								</td>
							</tr>
							<tr>
								<th scope="row">영업보증보험</th>
								<td>가입</td>
								<th scope="row">기획여행 보증보험</th>
								<td>가입</td>
							</tr>
						</tbody>
					</TableContainer>
					<style.SubTitle>여행경비</style.SubTitle>
					<style.ExpensesTable>
						<caption>여행경비</caption>
						<colgroup>
							<col style={{width: "17%"}} />
							<col style={{width: "17%"}} />
							<col style={{width: "22%"}} />
							<col style={{width: "22%"}} />
							<col style={{width: "22%"}} />
						</colgroup>
						<tbody>
							<tr>
								<th scope="col" colSpan={2}>
									구분
								</th>
								<th scope="col">성인</th>
								<th scope="col">아동</th>
								<th scope="col">유아</th>
							</tr>
							<tr>
								<style.ExpensesTd rowSpan={airTaxAdtCost + airTaxChdCost + airTaxInfCost > 0 ? 4 : 3} line={true}>
									상품가
								</style.ExpensesTd>
								<style.ExpensesTd>기본 상품가</style.ExpensesTd>
								<style.ExpensesTd align="right">{format.koKRFormatNumber(adtPrice)} 원</style.ExpensesTd>
								<style.ExpensesTd align="right">{format.koKRFormatNumber(chdPrice)} 원</style.ExpensesTd>
								<style.ExpensesTd align="right">{format.koKRFormatNumber(infPrice)} 원</style.ExpensesTd>
							</tr>
							<tr>
								<style.ExpensesTd>유류할증료</style.ExpensesTd>
								<style.ExpensesTd align="right">{format.koKRFormatNumber(bafAdtCost)} 원</style.ExpensesTd>
								<style.ExpensesTd align="right">{format.koKRFormatNumber(bafChdCost)} 원</style.ExpensesTd>
								<style.ExpensesTd align="right">{format.koKRFormatNumber(bafInfCost)} 원</style.ExpensesTd>
							</tr>
							{airTaxAdtCost + airTaxChdCost + airTaxInfCost > 0 && (
								<tr>
									<style.ExpensesTd>TAX</style.ExpensesTd>
									<style.ExpensesTd align="right">{format.koKRFormatNumber(airTaxAdtCost)} 원</style.ExpensesTd>
									<style.ExpensesTd align="right">{format.koKRFormatNumber(airTaxChdCost)} 원</style.ExpensesTd>
									<style.ExpensesTd align="right">{format.koKRFormatNumber(airTaxInfCost)} 원</style.ExpensesTd>
								</tr>
							)}
							<tr>
								<style.ExpensesTd line={true}>소계</style.ExpensesTd>
								<style.ExpensesTd align="right" line={true}>
									<RedText>
										{!isFH ? (
											<em>{format.koKRFormatNumber(totalPice.adult)} 원</em>
										) : (
											<em>{totalPice.adult > 0 ? `${format.koKRFormatNumber(totalPice.adult)} 원` : "별도문의"}</em>
										)}
									</RedText>
								</style.ExpensesTd>
								<style.ExpensesTd align="right" line={true}>
									<RedText>
										{!isFH ? (
											<em>{format.koKRFormatNumber(totalPice.child)} 원</em>
										) : (
											<em>{totalPice.child > 0 ? `${format.koKRFormatNumber(totalPice.child)} 원` : "별도문의"}</em>
										)}
									</RedText>
								</style.ExpensesTd>
								<style.ExpensesTd align="right" line={true}>
									<RedText>
										{!isFH ? (
											<em>{format.koKRFormatNumber(totalPice.infant)} 원</em>
										) : (
											<em>{totalPice.infant > 0 ? `${format.koKRFormatNumber(totalPice.infant)} 원` : "별도문의"}</em>
										)}
									</RedText>
								</style.ExpensesTd>
							</tr>
							<tr>
								<style.ExpensesTd rowSpan={3} line={true}>
									현지필수경비
								</style.ExpensesTd>
								<style.ExpensesTd>현지필수경비</style.ExpensesTd>
								<style.ExpensesTd align="right">
									{guideExpAmt} {expCurrCd}
								</style.ExpensesTd>
								<style.ExpensesTd align="right">
									{driverAmt} {expCurrCd}
								</style.ExpensesTd>
								<style.ExpensesTd align="right">-</style.ExpensesTd>
							</tr>
							<tr>
								<style.ExpensesTd line={true}>소계</style.ExpensesTd>
								<style.ExpensesTd align="right" line={true}>
									<RedText>
										<em>
											{guideExpAmt} {expCurrCd}
										</em>
									</RedText>
								</style.ExpensesTd>
								<style.ExpensesTd align="right" line={true}>
									<RedText>
										<em>
											{driverAmt} {expCurrCd}
										</em>
									</RedText>
								</style.ExpensesTd>
								<style.ExpensesTd align="right" line={true}>
									<RedText>
										<em>-</em>
									</RedText>
								</style.ExpensesTd>
							</tr>
						</tbody>
					</style.ExpensesTable>
					<style.BulletList>
						<BulletListItem>
							유류할증료 및 TAX는 유가 및 환율 등에 따라 변동될 수 있으며, 아동/유아 기준은 항공사마다 상이하여 예약 후
							최종 확정됩니다.
						</BulletListItem>
					</style.BulletList>
					<style.SubTitle>최소 출발 인원 및 수수료 정보</style.SubTitle>
					<TableContainer>
						<caption>최소 출발 인원 및 수수료 정보</caption>
						<colgroup>
							<col style={{width: "18%"}} />
							<col style={{width: "26%"}} />
							<col style={{width: "20%"}} />
							<col style={{width: "36%"}} />
						</colgroup>
						<tbody>
							<tr>
								<th scope="row">최소 출발 인원 기준</th>
								<td>{minCnt}명</td>
								<th scope="row">취소수수료 부과 기준</th>
								<td>
									<style.RadioList>
										<li>
											<Radio htmlFor="03" disabled defaultChecked={webCancelCommiYn === "Y"}>
												표준약관 적용
											</Radio>
										</li>
										<li>
											<Radio htmlFor="04" disabled defaultChecked={webCancelCommiYn === "N"}>
												특별약관 적용
											</Radio>
										</li>
									</style.RadioList>
								</td>
							</tr>
						</tbody>
					</TableContainer>
					<style.BulletList>
						<BulletListItem>
							최소출발인원 미충족 시 여행약관 제9조에 따라 여행출발 7일전까지 여행사는 여행계약을 일방적으로 해제하고
							여행자에게 통보할 수 있습니다.
						</BulletListItem>
						<BulletListItem>
							취소수수료는 계약금 입금일부터 적용됩니다. 취소수수료 부과 세부 기준을 반드시 확인하세요.
						</BulletListItem>
						<BulletListItem>
							특별약관 적용의 경우, 표준약관보다 높은 취소수료가 부과될 수 있으며, 별도의 동의 절차를 거치게 됩니다.
						</BulletListItem>
					</style.BulletList>
					<style.SubTitle>주요 확정/미정 정보</style.SubTitle>
					<TableContainer>
						<caption>주요 확정/미정 정보</caption>
						<colgroup>
							<col style={{width: "18%"}} />
							<col style={{width: "26%"}} />
							<col style={{width: "20%"}} />
							<col style={{width: "36%"}} />
						</colgroup>
						<tbody>
							<tr>
								<th scope="row">출발</th>
								<td>
									<style.RadioList>
										<li>
											<Radio htmlFor="03" disabled defaultChecked={webStartConfYn === "Y"}>
												확정
											</Radio>
										</li>
										<li>
											<Radio htmlFor="04" disabled defaultChecked={webStartConfYn === "N"}>
												미정
											</Radio>
										</li>
									</style.RadioList>
								</td>
								<th scope="row">여행일정</th>
								<td>
									<style.RadioList>
										<li>
											<Radio htmlFor="03" disabled defaultChecked={webTourDayYn === "Y"}>
												확정
											</Radio>
										</li>
										<li>
											<Radio htmlFor="04" disabled defaultChecked={webTourDayYn === "N"}>
												예정
											</Radio>
										</li>
									</style.RadioList>
								</td>
							</tr>
							<tr>
								<th scope="row">상품가격</th>
								<td>
									<style.RadioList>
										<li>
											<Radio htmlFor="03" disabled defaultChecked={webGoodsPriceYn === "Y"}>
												확정
											</Radio>
										</li>
										<li>
											<Radio htmlFor="04" disabled defaultChecked={webGoodsPriceYn === "N"}>
												미정
											</Radio>
										</li>
									</style.RadioList>
								</td>
								<th scope="row">항공</th>
								<td>
									<style.RadioList>
										<li>
											<Radio htmlFor="03" disabled defaultChecked={webAirConfYn === "Y"}>
												확정
											</Radio>
										</li>
										<li>
											<Radio htmlFor="04" disabled defaultChecked={webAirConfYn === "N"}>
												예정
											</Radio>
										</li>
									</style.RadioList>
								</td>
							</tr>
							<tr>
								<th scope="row">숙박</th>
								<td>
									<style.RadioList>
										<li>
											<Radio htmlFor="03" disabled defaultChecked={webAccommConfYn === "Y"}>
												확정
											</Radio>
										</li>
										<li>
											<Radio htmlFor="04" disabled defaultChecked={webAccommConfYn === "N"}>
												미정
											</Radio>
										</li>
									</style.RadioList>
								</td>
								<th scope="row">가이드</th>
								<td>
									<style.RadioList>
										<li>
											<Radio htmlFor="03" disabled defaultChecked={webGuidConfYn === "Y"}>
												있음
											</Radio>
										</li>
										<li>
											<Radio htmlFor="04" disabled defaultChecked={webGuidConfYn === "N"}>
												없음
											</Radio>
										</li>
									</style.RadioList>
								</td>
							</tr>
							<tr>
								<th scope="row">인솔자</th>
								<td colSpan={3}>
									<style.RadioList>
										<li>
											<Radio htmlFor="03" disabled defaultChecked={webInsolConfYn === "Y"}>
												동반
											</Radio>
										</li>
										<li>
											<Radio htmlFor="04" disabled defaultChecked={webInsolConfYn === "N"}>
												미동반
											</Radio>
										</li>
									</style.RadioList>
								</td>
							</tr>
						</tbody>
					</TableContainer>
					{shop && shop.length > 0 && (
						<>
							<style.SubTitle>
								쇼핑 정보
								<style.TableInfoText>
									아래 쇼핑 품목 중 총 <em>{shopCnt}번</em>의 쇼핑센터 방문이 일정에 포함되어 있습니다.(쇼핑 품목은
									여행일정에 따라 달라질 수 있습니다.)
								</style.TableInfoText>
							</style.SubTitle>
							<TableContainer types="vertical">
								<caption>쇼핑 정보</caption>
								<colgroup>
									<col style={{width: "12%"}} />
									<col style={{width: "36%"}} />
									<col style={{width: "20%"}} />
									<col style={{width: "12%"}} />
									<col style={{width: "20%"}} />
								</colgroup>
								<thead>
									<tr>
										<th scope="col">회차</th>
										<th scope="col">쇼핑 목록</th>
										<th scope="col">쇼핑 장소</th>
										<th scope="col">소요시간</th>
										<th scope="col">환불 여부</th>
									</tr>
								</thead>
								<tbody>
									{shop.map((item, index) => (
										<tr key={index}>
											<td>{index + 1}</td>
											<td>{item.shopNm}</td>
											<td>{item.shopPlace}</td>
											<td>{item.shopTm}</td>
											<td>{item.refundNote}</td>
										</tr>
									))}
								</tbody>
							</TableContainer>
						</>
					)}
					{shopInfo && (
						<>
							{shop.length === 0 && <style.SubTitle>쇼핑 정보</style.SubTitle>}
							<style.EditorWrap>
								<EditorBox dangerouslySetInnerHTML={{__html: shopInfo}} />
							</style.EditorWrap>
						</>
					)}
					{safety && (
						<>
							<style.SubTitle>
								여행지 안전 정보
								{safety.safeDate && (
									<style.TableInfoText>
										기준일시 : {dayjs(safety.safeDate).format("YYYY.MM.DD HH:mm:ss")}
									</style.TableInfoText>
								)}
							</style.SubTitle>
							<TableContainer types="vertical">
								<caption>여행지 안전 정보</caption>
								<colgroup>
									<col style={{width: "20%"}} />
									<col style={{width: "20%"}} />
									<col style={{width: "60%"}} />
								</colgroup>
								<tbody>
									<tr>
										<th scope="col" colSpan={2}>
											여행경보단계
										</th>
										<th scope="col">여행지(국가 또는 지역)</th>
									</tr>
									<tr>
										<td>남색경보</td>
										<td>여행유의</td>
										<td>
											<style.LocationText dangerouslySetInnerHTML={{__html: safety.attInfo || "-"}} />
										</td>
									</tr>
									<tr>
										<td>황색경보</td>
										<td>여행자제</td>
										<td>
											<style.LocationText dangerouslySetInnerHTML={{__html: safety.cntlInfo || "-"}} />
										</td>
									</tr>
									<tr>
										<td>적색경보</td>
										<td>철수권고</td>
										<td>
											<style.LocationText dangerouslySetInnerHTML={{__html: safety.limtInfo || "-"}} />
										</td>
									</tr>
									<tr>
										<td>흑색경보</td>
										<td>여행금지</td>
										<td>
											<style.LocationText dangerouslySetInnerHTML={{__html: safety.banNote || "-"}} />
										</td>
									</tr>
									<tr>
										<td colSpan={2}>
											특별 여행주의보
											<br />
											<style.InfoText>※ 단기적으로 긴급한 위험이 있는 국가(지역)에 대하여 발령합니다.</style.InfoText>
										</td>
										<td>
											<style.LocationText dangerouslySetInnerHTML={{__html: safety.splimitInfo || "-"}} />
										</td>
									</tr>
								</tbody>
							</TableContainer>
							<style.BulletList>
								<BulletListItem>
									특별여행주의보 발령에 따른 행동요령은 여행경보 2단계 이상 3단계 이하에 준합니다.
									<br />
									(예) (여행예정자) 여행취소·연기, (체류자) 신변안전 특별유의
									<br />
									특별여행주의보 발령 기간은 90일을 넘지 않습니다.
								</BulletListItem>
								<BulletListItem>
									외교부에서는 [여행경보제도]를 운영하고 있으며, 노랑풍선은 고객님의 안전한 해외여행을 위하여 이 정보를
									제공하고 있습니다. 여행경보단계는 여행유의/자제/철수권고/금지 4단계로 구분되며, 단기적인 위험상황이
									발생하는 경우 특별여행주의보/경보 2단계로 추가 구분하고 있습니다.
								</BulletListItem>
								<BulletListItem>
									외교부에서 국가별 상황에 따라 상시로 [여행경보제도]의 변경이 있을 수 있으므로, 당사에서 제공하는
									안전정보가 시점에 따라 다소 차이가 발생할 수 있사오니, 반드시 출국 전 여행목적지의(국가 및 지역,{" "}
									<a href="https://www.0404.go.kr/dev/main.mofa" target="_blank" rel="noreferrer">
										www.0404.go.kr
									</a>
									) 안전정보를 확인하시어 [외교부의 권고]를 따라주시기 바랍니다.
								</BulletListItem>
							</style.BulletList>
						</>
					)}
				</PrintArea>
			</PopupContainer>
		</Print>
	);
};

/** 여행상품 핵심정보 */
const KeyInformationPop = (props: OverlayState & Params) => (
	<ErrorBoundary logMsg="Popup KeyInformationPop Error">
		<KeyInformationPopComponent {...props} />
	</ErrorBoundary>
);

export default KeyInformationPop;
